import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProject } from '../contexts/ProjectContext';
import { useSettings } from '../contexts/SettingsContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';

const { FiSend, FiUser, FiAlertCircle } = FiIcons;
const { BiBot } = BiIcons;

const AIAssistant = () => {
  const { files, activeFile, updateFile } = useProject();
  const { settings } = useSettings();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Enhanced AI configuration check
  const isAIConfigured = () => {
    if (settings.aiProvider === 'local') {
      const runningModel = localStorage.getItem('runningLocalModel');
      return !!runningModel && !!window.localLLMEngine;
    }
    if (settings.aiProvider === 'openai' || settings.aiProvider === 'anthropic') {
      return settings.aiApiKey && settings.aiApiKey.trim().length > 0;
    }
    if (settings.aiProvider === 'custom') {
      return settings.aiApiKey && settings.customApiUrl && 
        settings.aiApiKey.trim().length > 0 && settings.customApiUrl.trim().length > 0;
    }
    return false;
  };

  // Smart context builder that limits content size
  const buildSmartContext = (userInput) => {
    let context = '';
    
    if (activeFile && files[activeFile]) {
      const fileContent = files[activeFile];
      const fileExtension = activeFile.split('.').pop()?.toLowerCase();
      
      // Limit file content based on size and type
      let limitedContent = fileContent;
      
      // For large files, provide intelligent excerpts
      if (fileContent.length > 2000) {
        if (fileExtension === 'html') {
          // For HTML, extract key parts
          const headMatch = fileContent.match(/<head[\s\S]*?<\/head>/i);
          const bodyStart = fileContent.match(/<body[^>]*>([\s\S]{0,500})/i);
          const bodyEnd = fileContent.match(/([\s\S]{0,500})<\/body>/i);
          
          limitedContent = `HTML Structure (truncated):
${headMatch ? headMatch[0] : '<!-- head section -->'}
${bodyStart ? `<body>${bodyStart[1]}...` : '<!-- body start -->'}
${bodyEnd ? `...${bodyEnd[1]}</body>` : '<!-- body end -->'}

[File is ${fileContent.length} characters total - showing key excerpts]`;
        } else if (fileExtension === 'css') {
          // For CSS, show first and last parts
          const lines = fileContent.split('\n');
          if (lines.length > 50) {
            const firstPart = lines.slice(0, 25).join('\n');
            const lastPart = lines.slice(-25).join('\n');
            limitedContent = `${firstPart}\n\n/* ... ${lines.length - 50} more lines ... */\n\n${lastPart}`;
          }
        } else if (fileExtension === 'js' || fileExtension === 'jsx') {
          // For JS, show functions and key parts
          const lines = fileContent.split('\n');
          if (lines.length > 50) {
            const firstPart = lines.slice(0, 30).join('\n');
            const lastPart = lines.slice(-20).join('\n');
            limitedContent = `${firstPart}\n\n// ... ${lines.length - 50} more lines ...\n\n${lastPart}`;
          }
        } else {
          // For other files, show beginning and end
          limitedContent = `${fileContent.substring(0, 1000)}\n\n... [${fileContent.length - 2000} characters omitted] ...\n\n${fileContent.substring(fileContent.length - 1000)}`;
        }
      }
      
      context = `Current file: ${activeFile}
File type: ${fileExtension?.toUpperCase() || 'Unknown'}
File content:
\`\`\`${fileExtension || 'text'}
${limitedContent}
\`\`\`

`;
    }

    // Check if user input is very long and provide guidance
    if (userInput.length > 3000) {
      context += `Note: User provided a long input (${userInput.length} characters). Please focus on the key parts and ask for clarification if needed.

`;
    }

    return context;
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Check if AI is configured
    if (!isAIConfigured()) {
      const errorMessage = {
        role: 'assistant',
        content: settings.aiProvider === 'local' 
          ? 'No local model is loaded. Please go to Settings > AI Configuration > Manage Models to download and load a model.'
          : 'API key not configured. Please add your API key in Settings.',
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await callAI(input);
      const aiMessage = { role: 'assistant', content: response };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.message}`,
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const callAI = async (userInput) => {
    // Build smart context that doesn't overwhelm the AI
    const context = buildSmartContext(userInput);
    const fullPrompt = `${context}User request: ${userInput}`;

    console.log('AI Assistant context length:', fullPrompt.length);

    if (settings.aiProvider === 'local') {
      return await callRealLocalModel(fullPrompt);
    } else if (settings.aiProvider === 'openai') {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${settings.aiApiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'You are a helpful coding assistant. Help users with their code, provide suggestions, and answer questions about programming. If the provided code is very long, focus on the relevant parts and ask clarifying questions if needed.'
            },
            {
              role: 'user',
              content: fullPrompt
            }
          ],
          max_tokens: 1500,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } else if (settings.aiProvider === 'anthropic') {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': settings.aiApiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-sonnet-20240229',
          max_tokens: 1500,
          messages: [{
            role: 'user',
            content: fullPrompt
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      return data.content[0].text;
    } else if (settings.aiProvider === 'custom' && settings.customApiUrl) {
      const response = await fetch(settings.customApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${settings.aiApiKey}`
        },
        body: JSON.stringify({
          prompt: fullPrompt,
          max_tokens: 1500
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      return data.response || data.text || data.content || 'No response received';
    } else {
      throw new Error('AI provider not configured properly');
    }
  };

  const callRealLocalModel = async (prompt) => {
    const runningModel = localStorage.getItem('runningLocalModel');
    console.log('🤖 Calling REAL local model:', runningModel);

    try {
      // Check if we have the WebLLM engine loaded
      if (!window.localLLMEngine) {
        throw new Error('Local model engine not initialized. Please load a model first.');
      }

      console.log('🚀 Using WebLLM engine for inference...');

      // Call the actual WebLLM model
      const response = await window.localLLMEngine.chat.completions.create({
        messages: [
          {
            role: 'system',
            content: 'You are a helpful coding assistant. Help users with their code, provide suggestions, and answer questions about programming. Be concise but thorough in your responses. If the provided code is very long, focus on the relevant parts.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1500,
        temperature: 0.7
      });

      const assistantResponse = response.choices[0].message.content;
      console.log('✅ Real local AI response received:', assistantResponse.substring(0, 100) + '...');
      return assistantResponse;
    } catch (error) {
      console.error('❌ Real local AI error:', error);
      
      // Provide helpful error messages
      let errorMessage = 'Local AI Error: ';
      if (error.message.includes('not initialized')) {
        errorMessage += 'Model not loaded. Please go to Settings > AI Configuration > Manage Models and load a model.';
      } else if (error.message.includes('memory')) {
        errorMessage += 'Not enough memory. Try closing other browser tabs or reload the page.';
      } else if (error.message.includes('network')) {
        errorMessage += 'Network error. The model might still be downloading.';
      } else {
        errorMessage += error.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  const applyCodeSuggestion = (code) => {
    if (activeFile) {
      updateFile(activeFile, code);
    }
  };

  const extractCodeFromMessage = (content) => {
    const codeMatch = content.match(/```[\s\S]*?\n([\s\S]*?)```/);
    return codeMatch ? codeMatch[1] : null;
  };

  const getAIProviderInfo = () => {
    const runningModel = localStorage.getItem('runningLocalModel');
    switch (settings.aiProvider) {
      case 'local':
        return {
          name: 'Local Model',
          status: runningModel ? `Running: ${runningModel}` : 'No model loaded',
          color: runningModel && window.localLLMEngine ? 'text-green-400' : 'text-red-400'
        };
      case 'openai':
        return {
          name: 'OpenAI GPT',
          status: settings.aiApiKey ? 'Connected' : 'Not configured',
          color: settings.aiApiKey ? 'text-green-400' : 'text-red-400'
        };
      case 'anthropic':
        return {
          name: 'Claude',
          status: settings.aiApiKey ? 'Connected' : 'Not configured',
          color: settings.aiApiKey ? 'text-green-400' : 'text-red-400'
        };
      case 'custom':
        return {
          name: 'Custom API',
          status: settings.aiApiKey && settings.customApiUrl ? 'Connected' : 'Not configured',
          color: settings.aiApiKey && settings.customApiUrl ? 'text-green-400' : 'text-red-400'
        };
      default:
        return {
          name: 'Unknown',
          status: 'Not configured',
          color: 'text-red-400'
        };
    }
  };

  const aiInfo = getAIProviderInfo();
  const configured = isAIConfigured();

  if (!configured) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-gray-800 flex-shrink-0">
          <h3 className="text-white font-semibold flex items-center text-sm">
            <SafeIcon icon={BiBot} className="mr-2 text-purple-400" />
            AI Assistant
          </h3>
          <div className="text-xs mt-1">
            <span className="text-gray-400">{aiInfo.name}: </span>
            <span className={aiInfo.color}>{aiInfo.status}</span>
          </div>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-gray-500">
            <SafeIcon icon={FiAlertCircle} className="text-6xl mb-4 mx-auto text-gray-600" />
            <p className="text-lg mb-2">AI Assistant Not Configured</p>
            <p className="text-sm mb-4">
              {settings.aiProvider === 'local' 
                ? 'Load a local model in Settings to enable AI features'
                : 'Configure your AI provider in Settings to enable AI features'
              }
            </p>
            <div className="text-xs text-gray-400">
              <p>• Local Models: Complete privacy, offline usage</p>
              <p>• Cloud APIs: More powerful, latest features</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex-shrink-0">
        <h3 className="text-white font-semibold flex items-center text-sm">
          <SafeIcon icon={BiBot} className="mr-2 text-purple-400" />
          AI Assistant
        </h3>
        <div className="text-xs mt-1">
          <span className="text-gray-400">{aiInfo.name}: </span>
          <span className={aiInfo.color}>{aiInfo.status}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-gray-500 text-center py-8">
            <SafeIcon icon={BiBot} className="text-4xl mb-3 mx-auto text-gray-600" />
            <p className="text-sm mb-2">Ask me anything about your code!</p>
            {settings.aiProvider === 'local' && (
              <p className="text-xs mt-2 text-purple-400">
                🔒 Using local AI - completely private
              </p>
            )}
            <div className="mt-4 text-xs text-gray-400">
              <p>💡 Try asking:</p>
              <p>"Fix this error" • "Add a button click" • "Explain this code"</p>
              <p className="mt-2 text-purple-400">📝 Paste large amounts of code directly in chat!</p>
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <motion.div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className={`max-w-[90%] p-3 rounded-xl text-sm ${
              message.role === 'user'
                ? 'bg-blue-600 text-white'
                : message.isError
                ? 'bg-red-900/50 border border-red-500/30 text-red-300'
                : 'bg-gray-900 border border-gray-700 text-gray-100'
            }`}>
              <div className="flex items-start gap-2">
                <SafeIcon
                  icon={message.role === 'user' ? FiUser : BiBot}
                  className="text-sm mt-1 flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {message.content}
                  </div>
                  {message.role === 'assistant' && !message.isError && (() => {
                    const code = extractCodeFromMessage(message.content);
                    return code ? (
                      <button
                        onClick={() => applyCodeSuggestion(code)}
                        className="mt-2 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 rounded text-xs font-medium transition-colors duration-200"
                      >
                        Apply Code
                      </button>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          </motion.div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-900 border border-gray-700 text-gray-100 p-3 rounded-xl">
              <div className="flex items-center gap-2">
                <SafeIcon icon={BiBot} className="text-sm" />
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                {settings.aiProvider === 'local' && (
                  <span className="text-xs text-purple-400 ml-2">
                    Processing locally...
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-800 flex-shrink-0">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about your code, paste large code snippets, or ask for help..."
            className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-purple-500 focus:outline-none text-white placeholder-gray-500 text-sm resize-none min-h-[40px] max-h-[120px]"
            disabled={loading}
            rows="1"
            style={{ 
              height: 'auto',
              minHeight: '40px',
              maxHeight: '120px',
              overflowY: input.length > 200 ? 'auto' : 'hidden'
            }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiSend} className="text-sm" />
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Press Enter to send • Shift+Enter for new line • Handles long inputs intelligently
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;